import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE_LIGHT, GRAY, GRAY_LIGHTER} from 'ui-library/Colors';

import {
  AnalysisOutputSelectorOption,
  isOutputOptionDisabled,
  runSaveToResultsModal,
} from './AnalysisOutputSelectorOption';
import {AnalysisOutput, AnalysisOutputConfig, AnalysisOutputOptionItem, AnalysisOutputType} from './Types';
import usePossibleOutputOptions from './usePossibleOutputOptions';

function SelectAllButton({
  possibleOutputOptions,
  onUpdateSelectedOutputs,
}: {
  possibleOutputOptions: Array<AnalysisOutputOptionItem>;
  onUpdateSelectedOutputs: (newSelectedOutputs: Array<AnalysisOutput>) => void;
}): JSX.Element | null {
  const analysisOutputOptionItems = possibleOutputOptions
    .filter(
      (possibleOutputOption) =>
        !isOutputOptionDisabled(possibleOutputOption) &&
        // We can't return a result since newDataMapping can't be set by select all
        possibleOutputOption.outputConfigs.find(
          (outputConfig) => outputConfig.type !== AnalysisOutputType.RESULT
        ) != null
    )
    .map((possibleOutputOption) => {
      const outputConfig = possibleOutputOption.outputConfigs.find(
        (outputConfig) => outputConfig.type !== AnalysisOutputType.RESULT
      )! as AnalysisOutputConfig;
      return {...possibleOutputOption, outputConfig};
    });

  return (
    <span
      className={css(styles.selectAll)}
      onClick={() => {
        onUpdateSelectedOutputs(analysisOutputOptionItems);
      }}
      {...getTestProps({component: 'AnalysisOutputSelector', element: 'selectAllButton'})}
    >
      Select all
    </span>
  );
}

export interface AnalysisOutputSelectorProps {
  pipeline: PipelineGraphQL;
  clientStateId: string;
  selectedOutputs: Array<AnalysisOutput>;
  onUpdateSelectedOutputs: (newSelectedOutputs: Array<AnalysisOutput>) => void;
  shouldEnableOutputFromStep?: (step: GetAnalysisPipelineStep) => string | null;
}

export default function AnalysisOutputSelector({
  pipeline,
  clientStateId,
  onUpdateSelectedOutputs,
  selectedOutputs,
  shouldEnableOutputFromStep,
}: AnalysisOutputSelectorProps): JSX.Element {
  const possibleOutputOptions = usePossibleOutputOptions(pipeline, shouldEnableOutputFromStep);
  function handleUpdateSelectedOutputs(
    {outputConfigs, ...baseAnalysisOutput}: AnalysisOutputOptionItem,
    isSelected: boolean
  ): void {
    if (isSelected) {
      const outputConfig = outputConfigs[0];
      if (outputConfig.type !== AnalysisOutputType.RESULT) {
        onUpdateSelectedOutputs([
          ...selectedOutputs,
          {
            ...baseAnalysisOutput,
            outputConfig: outputConfig as AnalysisOutputConfig,
          },
        ]);
      } else {
        runSaveToResultsModal(
          clientStateId,
          outputConfig.dataFrame,
          ({dataMapping, targetSpecificMappingState}) => {
            onUpdateSelectedOutputs([
              ...selectedOutputs,
              {
                ...baseAnalysisOutput,
                outputConfig: {...outputConfig, newDataMapping: dataMapping, targetSpecificMappingState},
              },
            ]);
          }
        );
      }
    } else {
      onUpdateSelectedOutputs(
        selectedOutputs.filter(({selectorIndex}) => selectorIndex !== baseAnalysisOutput.selectorIndex)
      );
    }
  }
  return (
    <div
      className={css(styles.selectorWrapper)}
      {...getTestProps({component: 'AnalysisOutputSelector', element: 'root'})}
    >
      <div className={css(styles.selectorHeader)}>
        {selectedOutputs.length} selected
        <SelectAllButton
          possibleOutputOptions={possibleOutputOptions}
          onUpdateSelectedOutputs={onUpdateSelectedOutputs}
        />
      </div>
      <div className={classNames('scrollbar-override', css(styles.selectorContent))}>
        {possibleOutputOptions.map((option, index) => (
          <AnalysisOutputSelectorOption
            key={`analysisOutputSelectorOption-${index}`}
            clientStateId={clientStateId}
            option={option}
            onUpdateSelectedOutput={(isSelected: boolean) => {
              handleUpdateSelectedOutputs(option, isSelected);
            }}
            onUpdateOutputConfig={(newOutputConfig) => {
              const indexToUpdate = selectedOutputs.findIndex(
                (selectedOutput) => selectedOutput.selectorIndex === option.selectorIndex
              );
              const newSelectedOutputs = [...selectedOutputs];
              newSelectedOutputs[indexToUpdate] = {
                ...selectedOutputs[indexToUpdate],
                outputConfig: newOutputConfig,
              };
              onUpdateSelectedOutputs(newSelectedOutputs);
            }}
            selectedOutputs={selectedOutputs}
          />
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  selectorWrapper: {
    border: 'solid 1px',
    borderColor: GRAY,
    borderRadius: 4,
    ...FONT_PROXIMA_13,
  },
  selectorHeader: {
    padding: '10px 8px 10px 8px',
    backgroundColor: GRAY_LIGHTER,
  },
  selectAll: {
    color: BLUE_LIGHT,
    cursor: 'pointer',
    marginLeft: 16,
  },
  // Don't include top padding because individual items each have their own top margin
  selectorContent: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 300,
    overflowY: 'auto',
    gap: 6,
  },
});
